var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx } from "react/jsx-runtime";
import { AssetStatusType } from '@design-stack-vista/vista-assets-sdk';
import { observer } from 'mobx-react-lite';
import { createContext, useContext, useMemo, useState, useCallback } from 'react';
import { useUploadManager } from '../../../../UploadManagerProvider';
import { useAsyncEffect } from '../../../../UploadManagerProvider/hooks/useAsyncEffect';
import { getNumPages } from '../../utils';
const DrawerProviderContext = createContext(undefined);
const { Provider } = DrawerProviderContext;
/**
 * Provider context for the DrawerProvider
 */
export const useDrawerThumbnailAsset = () => {
    const result = useContext(DrawerProviderContext);
    if (!result) {
        throw new Error('Should be used inside an DrawerThumbnailAssetProvider.');
    }
    return result;
};
/**
 * Provider whose context provides the ARIA labels for the ui elements as well as if an asset is used on the design.
 */
export const DrawerThumbnailAssetProvider = observer((props) => {
    var _a, _b;
    const { children, asset, asset: { status }, pageNumber, } = props;
    const type = status.type;
    const { updateVisibleAssets } = useUploadManager();
    const [showProcessing, setShowProcessing] = useState(type === AssetStatusType.Preparing);
    const [isError, setIsError] = useState(false);
    const onThumbnailAssetError = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        var _c;
        setIsError(true);
        // we don't want to hide the original asset if a non-initial page failed.  Other pages may have succeeded.
        // however if page 1 failed probably everything fails
        if (pageNumber === 1) {
            // if we don't have an id this will fail
            if ((_c = asset.data) === null || _c === void 0 ? void 0 : _c.id) {
                yield asset.hide();
            }
            updateVisibleAssets();
        }
    }), [asset, updateVisibleAssets, pageNumber]);
    useAsyncEffect((helpers) => {
        (() => __awaiter(void 0, void 0, void 0, function* () {
            var _a, _b, _c, _d, _e, _f, _g, _h;
            if (((_c = (_b = (_a = asset.data) === null || _a === void 0 ? void 0 : _a.info) === null || _b === void 0 ? void 0 : _b.image) === null || _c === void 0 ? void 0 : _c.format) === 'unknown') {
                onThumbnailAssetError();
            }
            else if (((_e = (_d = asset.data) === null || _d === void 0 ? void 0 : _d.info) === null || _e === void 0 ? void 0 : _e.image) && 'numPages' in asset.data.info.image) {
                if (asset.isDamAsset()) {
                    const pageImageInfo = yield asset.getPageImageInfo({ pageNo: pageNumber });
                    if (pageImageInfo.isError) {
                        helpers.runIfMounted(onThumbnailAssetError);
                    }
                }
            }
            else if ((_h = (_g = (_f = asset.data) === null || _f === void 0 ? void 0 : _f.info) === null || _g === void 0 ? void 0 : _g.image) === null || _h === void 0 ? void 0 : _h.isError) {
                onThumbnailAssetError();
            }
        }))();
    }, [asset, (_b = (_a = asset.data) === null || _a === void 0 ? void 0 : _a.info) === null || _b === void 0 ? void 0 : _b.image, onThumbnailAssetError, pageNumber]);
    const value = useMemo(() => ({
        asset,
        pageNumber,
        showProcessing,
        setShowProcessing,
        isMultiPage: getNumPages(asset) > 1,
        isError,
        onThumbnailAssetError,
    }), [asset, pageNumber, showProcessing, setShowProcessing, isError, onThumbnailAssetError]);
    return _jsx(Provider, Object.assign({ value: value }, { children: children }));
});
DrawerThumbnailAssetProvider.displayName = 'DrawerThumbnailAssetProvider';
//# sourceMappingURL=DrawerThumbnailAssetProvider.js.map